import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { UserPlus, Loader2, Trash2, Mail, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const apiErr = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";
const ROLES = [
  { v: "manager", d: "Campaigns, leads, call logs, analytics — no settings/billing" },
  { v: "agent", d: "View leads & call logs only — cannot trigger calls or edit" },
  { v: "viewer", d: "Read-only dashboard & analytics" },
  { v: "admin", d: "Full access to everything" },
];
const roleColor = { admin: "bg-indigo-100 text-indigo-700", client: "bg-indigo-100 text-indigo-700", manager: "bg-blue-100 text-blue-700", agent: "bg-emerald-100 text-emerald-700", viewer: "bg-slate-100 text-slate-600" };
const statusColor = { active: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700", suspended: "bg-red-100 text-red-700" };

export default function TeamTab() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "manager" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/team").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email.trim()) { toast.error("Enter an email"); return; }
    setBusy(true);
    try { const { data } = await api.post("/team/invite", form); toast.success(data.message); setOpen(false); setForm({ name: "", email: "", role: "manager" }); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const changeRole = async (uid, role) => { try { await api.post(`/team/${uid}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleStatus = async (m) => { const s = m.status === "suspended" ? "active" : "suspended"; try { await api.post(`/team/${m.id}/status`, { status: s }); toast.success(`Member ${s}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const remove = async (m) => { if (!window.confirm(`Remove ${m.name || m.email}? This cannot be undone.`)) return; try { await api.delete(`/team/${m.id}`); toast.success("Member removed"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const resend = async (uid) => { try { const { data } = await api.post(`/team/invite/${uid}/resend`); toast.success(data.message); load(); } catch (e) { toast.error(apiErr(e)); } };
  const cancel = async (uid) => { try { await api.post(`/team/invite/${uid}/cancel`); toast.success("Invitation cancelled"); load(); } catch (e) { toast.error(apiErr(e)); } };

  if (!data) return <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-500 mx-auto" /></div>;

  return (
    <Card className="border shadow-none p-6 max-w-3xl" data-testid="team-tab">
      <div className="flex items-center justify-between mb-4">
        <div><h3 className="font-semibold font-display">Team Members</h3><p className="text-xs text-slate-500">Invite teammates and control what they can access.</p></div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="invite-member-btn"><UserPlus className="w-4 h-4 mr-1" />Invite Member</Button>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500"><tr>
            {["Name", "Email", "Role", "Last Login", "Status", "Actions"].map((h) => <th key={h} className="text-left px-3 py-2.5 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.id} className="border-t" data-testid={`team-row-${m.id}`}>
                <td className="px-3 py-2.5 font-medium">{m.name || "—"}{m.is_owner && <span className="ml-1 text-[10px] text-indigo-500">(Owner)</span>}</td>
                <td className="px-3 py-2.5 text-slate-500">{m.email}</td>
                <td className="px-3 py-2.5">
                  {m.is_owner ? <Badge className={roleColor.client}>Admin</Badge> : (
                    <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                      <SelectTrigger className="h-8 w-28" data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.v[0].toUpperCase() + r.v.slice(1)}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </td>
                <td className="px-3 py-2.5 text-slate-400 text-xs">{m.last_login ? new Date(m.last_login).toLocaleDateString() : "—"}</td>
                <td className="px-3 py-2.5"><Badge className={statusColor[m.status] || statusColor.active}>{m.status || "active"}</Badge></td>
                <td className="px-3 py-2.5">
                  {!m.is_owner && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toggleStatus(m)} data-testid={`toggle-${m.id}`}>{m.status === "suspended" ? "Activate" : "Suspend"}</Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => remove(m)} data-testid={`remove-${m.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.pending?.length > 0 && (
        <div className="mt-5">
          <h4 className="text-sm font-semibold mb-2">Pending Invitations</h4>
          <div className="space-y-2">
            {data.pending.map((p) => (
              <div key={p.id} className="flex items-center gap-3 rounded-lg border p-2.5 text-sm" data-testid={`pending-${p.team_member_id}`}>
                <Mail className="w-4 h-4 text-amber-500" />
                <span className="flex-1">{p.name} <span className="text-slate-400">· {p.email} · {p.role}</span></span>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => resend(p.team_member_id)}><RefreshCw className="w-3 h-3 mr-1" />Resend</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => cancel(p.team_member_id)}><X className="w-3 h-3 mr-1" />Cancel</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Invite Team Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="invite-name" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="teammate@company.com" data-testid="invite-email" /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.v[0].toUpperCase() + r.v.slice(1)} — {r.d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={invite} disabled={busy} data-testid="send-invite">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mail className="w-4 h-4 mr-1" />}Send Invitation</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
